<template>
  <div class="settings-page fade-in">
    <!-- ===== Global Loader ===== -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ loaderText }}</p>
      </div>
    </transition>

    <!-- ===== Row 1: Tabs ===== -->
    <div class="tabs-container">
      <div
        v-for="tab in tabs"
        :key="tab"
        class="tab-item"
        :class="{ active: activeTab === tab }"
        @click="activeTab = tab"
      >
        {{ tab }}
      </div>
    </div>

    <!-- ===== ACCOUNT SETTINGS ===== -->
    <div v-if="activeTab === 'Account'" class="settings-section">
      <div class="account-flex">
        <!-- === Basic Profile === -->
        <div class="card half-card">
          <h3>Basic Profile</h3>
          <div class="profile-pic-upload">
            <label for="profilePicInput" class="profile-pic-label">
              <div class="profile-pic-frame">
                <img
                  v-if="avatarPreview"
                  :src="avatarPreview"
                  alt="Profile Picture"
                />
                <i v-else class="bx bx-user default-icon"></i>
              </div>
              <div class="upload-overlay"><i class="bx bx-camera"></i></div>
            </label>
            <input
              type="file"
              id="profilePicInput"
              accept="image/*"
              @change="handleAvatarUpload"
            />
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label>Full Name</label>
              <input type="text" v-model="profileForm.fullname" placeholder="John Doe" />
            </div>
            <div class="form-group">
              <label>Email Address</label>
              <input type="email" v-model="profileForm.email" placeholder="john@example.com" readonly/>
            </div>
          </div>
          <div class="form-group" style="margin-top: 1rem;">
            <label>Phone Number</label>
            <input type="text" v-model="profileForm.mobile" placeholder="+1 987 654 3210" />
          </div>

          <div class="button-row">
            <button class="btn-primary" @click="updateProfileNow">
              <i class="bx bx-save"></i> Update Profile
            </button>
          </div>
        </div>

        <!-- === Change Password === -->
        <div class="card half-card">
          <h3>Change Password</h3>
          <div>
            <div class="form-group">
              <label>Current Password</label>
              <input type="password" v-model="passwordForm.old_pass" placeholder="Enter current password" />
            </div>
            <div class="form-group" style="margin-top: 1rem;">
              <label>New Password</label>
              <input type="password" v-model="passwordForm.new_pass" placeholder="Enter new password" />
            </div>
            <div class="form-group" style="margin-top: 1rem;">
              <label>Re-Enter New Password</label>
              <input type="password" v-model="passwordForm.new_pass1" placeholder="Enter new password" />
            </div>
          </div>

          <div class="button-row">
            <button class="btn-primary" @click="updatePasswordNow">
              <i class="bx bx-lock"></i> Update Password
            </button>
          </div>
        </div>
      </div>

      <!-- === Delete Account === -->
      <div class="card full-width">
        <h3>Delete Account</h3>
        <p>Deleting your account is irreversible. All data will be lost permanently.</p>
        <button class="btn-danger" @click="showDeleteDialog = true" style="margin-top: 1rem;">
          <i class="bx bx-trash"></i> Delete Account
        </button>
      </div>
    </div>

    <!-- ===== BRAND SETTINGS ===== -->
    <div v-if="activeTab === 'Brand'" class="settings-section">
      <div class="card">
        <h3>Brand Information</h3>
        <div class="form-grid">
          <div class="form-group">
            <label>Logo</label>
            <input type="file" @change="handleLogoUpload" />
          </div>
          <div class="form-group">
            <label>Favicon</label>
            <input type="file" @change="handleFavUpload" />
          </div>
        </div>

        <div class="form-grid" style="margin-top: 1rem;">
          <div class="form-group">
            <label>Brand Name</label>
            <input type="text" v-model="businessForm.business_name" placeholder="Your Brand Name" />
          </div>
          <!-- <div class="form-group">
            <label>Punch Line</label>
            <input type="text" v-model="businessForm.business_punchline" placeholder="Your Tagline" />
          </div> -->
        </div>

        <div class="form-group" style="margin-top: 1rem;">
          <label>About Business</label>
          <textarea rows="3" v-model="businessForm.business_about" placeholder="Describe your business briefly"></textarea>
        </div>

        <!-- <div class="form-grid" style="margin-top: 1rem;">
          <div class="form-group">
            <label>Primary Color</label>
            <input type="color" v-model="businessForm.business_primary" />
          </div>
          <div class="form-group">
            <label>Secondary Color</label>
            <input type="color" v-model="businessForm.business_secondary" />
          </div>
        </div> -->
        <br />
        <button class="btn-primary mt-2" @click="updateBusinessNow">
          <i class="bx bx-save"></i> Save Brand Settings
        </button>
      </div>
    </div>

    <!-- ===== PAYMENT SETTINGS ===== -->
    <div v-if="activeTab === 'Payment'" class="settings-section payment-section">
      <div class="payment-grid">
        <div
          class="payment-card-modern fade-in"
          v-for="(pay, i) in paymentGateways"
          :key="i"
        >
          <div class="payment-header">
            <div class="icon-bubble" :style="'background-color:'+pay.color+';'"><i :class="pay.icon"></i></div>
            <div class="header-info">
              <h4>{{ pay.name }}</h4>
              <span
                class="status-chip"
                :class="pay.connected ? 'status-connected' : 'status-disconnected'"
                
              >
                <i :class="pay.connected ? 'bx bx-check-circle' : 'bx bx-x-circle'"></i>
                {{ pay.connected ? 'Connected' : 'Not Connected' }}
              </span>
            </div>
          </div>

          <div class="divider"></div>
          <div class="payment-footer">
            <button
              :class="pay.connected ? 'btn-secondary-modern' : 'btn-primary-modern'"
              @click="toggleIntegration('payment', pay)"
            >
              <i :class="pay.connected ? 'bx bx-unlink' : 'bx bx-link-alt'"></i>
              {{ pay.connected ? 'Disconnect' : 'Connect' }}
            </button>
            <!-- <button class="btn-outline-modern"><i class="bx bx-info-circle"></i> Details</button> -->
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CALENDAR SETTINGS ===== -->
    <div v-if="activeTab === 'Calendar'" class="settings-section payment-section">
      <div class="payment-grid">
        <div class="payment-card-modern fade-in" v-for="(cal, i) in calendars" :key="i">
          <div class="payment-header">
            <div class="icon-bubble" :style="'background-color:'+cal.color+';'"><i :class="cal.icon"></i></div>
            <div class="header-info">
              <h4>{{ cal.name }}</h4>
              <span
                class="status-chip"
                :class="cal.connected ? 'status-connected' : 'status-disconnected'"
              >
                <i :class="cal.connected ? 'bx bx-check-circle' : 'bx bx-x-circle'"></i>
                {{ cal.comming ?"Comming Soon":cal.connected ? 'Connected' : 'Not Connected' }}
              </span>
            </div>
          </div>

          <div class="divider"></div>
          <div class="payment-footer">
            <button
              :class="cal.connected ? 'btn-secondary-modern' : 'btn-primary-modern'"
              @click="toggleIntegration('calendar', cal)"
            >
              <i :class="cal.connected ? 'bx bx-unlink' : 'bx bx-link-alt'"></i>
              {{ cal.connected ? 'Disconnect' : 'Connect' }}
            </button>
            <!-- <button class="btn-outline-modern"><i class="bx bx-info-circle"></i> Details</button> -->
          </div>
        </div>
      </div>
    </div>

    <!-- ===== INTEGRATIONS (Email + Templates) ===== -->
    <div v-if="activeTab === 'Integrations'" class="settings-section integrations-section" style="height: 80vh;">
      <div class="integrations-row fade-in">
        <div class="integration-card-box">
          <div class="integration-header">
            <i class="bx bx-envelope"></i>
            <div>
              <h3>Email Integration</h3>
              <p>Connect your business email for automated confirmations and notifications.</p>
            </div>
          </div>
          <button class="btn-primary" @click="toggleIntegration('email', emailIntegration)">
            <i class="bx bx-link-alt"></i>
            {{ emailIntegration.connected ? "Disconnect" : "Connect" }}
          </button>
        </div>

       
      </div>

      <!-- Email Templates -->
      <div class="card fade-in">
        <div class="grid-header">
          <div class="search-box">
            <i class="bx bx-search"></i>
            <input type="text" v-model="searchEmail" placeholder="Search email templates..." />
          </div>
          <button class="btn-primary" @click="showEmailModal = true">
            <i class="bx bx-plus"></i> Add Template
          </button>
        </div>

        <div class="template-list">
          <div
            v-for="temp in filteredEmailTemplates"
            :key="temp.temp_id"
            class="template-item"
          >
            <div>
              <h4>{{ temp.temp_title }}</h4>
              <p>Body: {{ temp.temp_desc }}</p>
            </div>
            <div>
              <button class="btn-secondary" @click="editTemplate(temp, 'email')">Edit</button>
              <button class="btn-danger" @click="deleteTemplateNow(temp.temp_id)">Delete</button>
            </div>
          </div>
        </div>
        <div class="pagination" v-if="emailTotal > limit">
  <div class="pagination-buttons">
    <button
      v-for="n in Math.ceil(emailTotal / limit)"
      :key="n"
      :class="{ active: emailPage === n - 1 }"
      @click="changeEmailPage(n - 1)"
    >
      {{ n }}
    </button>
  </div>
</div>
<br><br><br><br><br><br>

      </div>
    </div>

    <!-- ===== Notifications Templates ===== -->
    <div v-if="activeTab === 'Notifications'" class="settings-section" style="height: 80vh;">
      <div class="card">
        <div class="grid-header">
          <div class="search-box">
            <i class="bx bx-search"></i>
            <input type="text" v-model="searchNotif" placeholder="Search notification templates..." />
          </div>
          <button class="btn-primary" @click="showNotifModal = true">
            <i class="bx bx-plus"></i> Add Template
          </button>
        </div>

        <div class="template-list">
          <div
            v-for="temp in filteredNotifTemplates"
            :key="temp.temp_id"
            class="template-item"
          >
            <div>
              <h4>{{ temp.temp_title }}</h4>
              <p>Body: {{ temp.temp_desc }}</p>
            </div>
            <div>
              <button class="btn-secondary" @click="editTemplate(temp, 'notification')">Edit</button>
              <button class="btn-danger" @click="deleteTemplateNow(temp.temp_id)">Delete</button>
            </div>
          </div>
        </div>
        <div class="pagination" v-if="notifTotal > limit">
  <div class="pagination-buttons">
    <button
      v-for="n in Math.ceil(notifTotal / limit)"
      :key="n"
      :class="{ active: notifPage === n - 1 }"
      @click="changeNotifPage(n - 1)"
    >
      {{ n }}
    </button>
  </div>
</div>

      </div>
    </div>

    <div v-if="activeTab === 'Billing'" class="settings-section">
       <div class="plans-header">
      <h2>Choose the Perfect Plan for Your Business</h2>
      <p>
        Scale your operations with flexible membership plans built to match your growth.
        Upgrade anytime — unlock premium analytics, automation, and more.
      </p>
      <div class="cta-actions">
        <button class="btn-primary"><i class="bx bx-rocket"></i> Get Started</button>
        <button class="btn-secondary"><i class="bx bx-help-circle"></i> Learn More</button>
      </div>
    </div>
       <div class="plans-container">
      <div
        v-for="(plan, i) in plans"
        :key="i"
        :class="['plan-card', { featured: plan.featured }]"
      >
        <!-- Icon + Plan Name -->
        <div class="plan-header">
          <i :class="plan.icon"></i>
          <h3>{{ plan.plan_title || plan.name }}</h3>
          <p class="plan-subtitle" v-if="plan.featured">Most Popular</p>
        </div>

        <!-- Price Section -->
        <div class="plan-price">
          <h2>${{ plan.plan_price || plan.price }}
          <small class="duration">/ {{ plan.plan_duration || plan.duration }}</small>
          </h2>
          <p>{{ plan.plan_desc || "" }}</p>
        </div>

        <!-- Features List -->
        <ul class="plan-features">
          <li v-for="(feat, j) in plan.frt || plan.features" :key="j">
            <i class="bx bx-check-circle"></i> {{ feat }}
          </li>
        </ul>

        <!-- Action Button -->
        <button
          :disabled="plan.my_plan === 1"
          :class="plan.my_plan === 1 ? 'btn-disabled wide-btn' : plan.featured ? 'btn-primary wide-btn' : 'btn-secondary wide-btn'"
          @click="plan.my_plan === 1 ? null : openUpgradePopup(plan)"
        >
          <i v-if="plan.my_plan === 1" class="bx bx-check-shield"></i>
          <i v-else class="bx bx-upvote"></i>
          {{ plan.my_plan === 1 ? 'Current Plan (' + remainingDays() + ' days left)' : 'Upgrade Plan' }}
        </button>
      </div>
    </div>
    </div>
<div v-if="showPaymentPopup" class="payment-popup-overlay" @click.self="closePopup">
  <div class="payment-popup">
    <h3>Choose Your Payment Gateway</h3>
    <p>Securely upgrade your plan using your preferred payment method.</p>
    <div class="gateway-options">
      <div class="gateway razorpay" @click="selectGateway('razorpay')">
        <i class="bx bx-credit-card gateway-icon razorpay-icon"></i>
        <span>Razorpay</span>
      </div>
      <div class="gateway paypal" @click="selectGateway('paypal')">
        <i class="bx bxl-paypal gateway-icon"></i>
        <span>PayPal</span>
      </div>
      <div class="gateway stripe" @click="selectGateway('stripe')">
        <i class="bx bxl-stripe gateway-icon"></i>
        <span>Stripe</span>
      </div>
    </div>
    <button class="btn-secondary close-btn" @click="closePopup">Cancel</button>
  </div>
</div>
    <!-- === Custom Delete Account Confirmation === -->
<transition name="fade">
  <div v-if="showDeleteDialog" class="confirm-overlay">
    <div class="confirm-container">
      <div class="confirm-icon">
        <i class="bx bx-error-circle"></i>
      </div>
      <h3>Delete Your Account?</h3>
      <p>
        This action <strong>cannot be undone</strong>.  
        All your business data, invoices, and clients will be permanently deleted.
      </p>

      <div class="confirm-actions">
        <button class="btn-cancel" @click="showDeleteDialog = false">
          <i class="bx bx-x"></i> Cancel
        </button>
        <button class="btn-danger-confirm" @click="deleteAccountNow">
          <i class="bx bx-trash"></i> Yes, Delete Permanently
        </button>
      </div>
    </div>
  </div>
</transition>


   <!-- === Template Modals (Email / Notification) === -->
<TemplateModal
  v-if="showEmailModal"
  title="Add Email Template"
  @close="showEmailModal = false"
  @save="saveTemplate"
  :form="templateForm"
  :userId="userId"
  tempType="email"
/>

<TemplateModal
  v-if="showNotifModal"
  title="Add Notification Template"
  @close="showNotifModal = false"
  @save="saveTemplate"
  :form="templateForm"
  :userId="userId"
  tempType="notification"
/>


  </div>
</template>

<script>
import { getPlanList } from "@/apis/planapi";
import {
  updateProfile,
  updateBusinessProfile,
  changePassword,
  deactivateAccount,
  checkIntegration,
  getTemplates,
  createTemplate,
  deleteTemplate,
} from "@/apis/adminapis";
import { getUserById } from "@/apis/authapis";
import { useToast } from "vue-toastification";
import TemplateModal from "@/comps/TemplateModal.vue";
import axios from "axios";
import { apiBaseUrl } from "@/apis/appconst";
import { validateField } from "@/other/validator";


export default {
  name: "SettingsPage",
  components: { TemplateModal },

  data() {
    return {
      plans: [],
       emailPage: 0,
    notifPage: 0,
    emailTotal: 0,
    notifTotal: 0,
    showPaymentPopup: false,
      selectedPlan: null,
    limit:10,
      activeTab: "Account",
      tabs: ["Account", "Payment", "Calendar","Billing"],
      // tabs: ["Account", "Brand", "Payment", "Calendar","Integrations","Billing"],

      // === Loaders & Toast ===
      loading: false,
      loaderText: "Please wait...",
      toast: useToast(),

      // === Profile ===
      profileForm: {
        fullname: "",
        email: "",
        mobile: "",
      },
      avatarFile: null,
      avatarPreview: "",

      // === Password ===
      passwordForm: {
        old_pass: "",
        new_pass: "",
        new_pass1: "",
      },

      // === Business Profile ===
      businessForm: {
        business_name: "",
        business_punchline: "",
        business_about: "",
        business_primary: "#2563EB",
        business_secondary: "#F59E0B",
      },
      business_logo: null,
      business_fav: null,

      // === Delete Account Dialog ===
      showDeleteDialog: false,

      // === Payment / Calendar Integrations ===
      paymentGateways: [
        {
          name: "PayPal",
          connected: false,
          icon: "bx bxl-paypal",
          color: "#00457C",   // PayPal Blue
          comming: false
        },
        {
          name: "Stripe",
          connected: false,
          icon: "bx bxl-stripe",
          color: "#635BFF",   // Stripe Purple
          comming: false
        },
        // {
        //   name: "Razorpay",
        //   connected: false,
        //   icon: "bx bx-money",
        //   color: "#0B72E7",   // Razorpay Blue
        //   comming: false
        // },
      ],

      calendars: [
        {
          name: "Google",
          connected: false,
          icon: "bx bxl-google",
          color: "#DB4437",   // Google Blue
          comming: false
        },
        {
          name: "Outlook",
          connected: false,
          icon: "bx bxl-microsoft",
          color: "#0078D4",   // Microsoft Outlook Blue
          comming: true
        },
        {
          name: "Apple",
          connected: false,
          icon: "bx bxl-apple",
          color: "#000000",   // Apple Black
          comming: true
        },
      ],

      emailIntegration: { name: "Email", connected: false },
      notificationsEnabled: true,

      // === Templates ===
      emailTemplates: [],
      notifTemplates: [],
      searchEmail: "",
      searchNotif: "",

      // === Template Modals ===
      showEmailModal: false,
      showNotifModal: false,
      templateForm: {
        temp_id: "",
        temp_title: "",
        temp_desc: "",
        temp_type: "",
      },
    };
  },
  
  computed: {
    userId() {
      const ldata = JSON.parse(sessionStorage.getItem("ldata")) || {};
      return ldata.user_id || "";
    },
    filteredEmailTemplates() {
      if (!this.searchEmail.trim()) return this.emailTemplates;
      return this.emailTemplates.filter((t) =>
        t.temp_title.toLowerCase().includes(this.searchEmail.toLowerCase())
      );
    },
    filteredNotifTemplates() {
      if (!this.searchNotif.trim()) return this.notifTemplates;
      return this.notifTemplates.filter((t) =>
        t.temp_title.toLowerCase().includes(this.searchNotif.toLowerCase())
      );
    },
  },

 
  async mounted() {
    this.fetchPlans();

    try{
       const type = this.$route.query.type;
       let msg = this.$route.query.msg;

        if (msg) {
          msg = decodeURIComponent(msg);  // 🔥 DECODE FIX
        }

      if (type) {
        if(type === "error"){
          this.toast.error(msg);
        }
        else{
          this.toast.success(msg);
        }
      }
    }
    catch(e){
      console.log("sdfsad",e);
    }

    try {
      const rtype = this.$route.query.rtype;

      if (rtype) {
        if (rtype === "calender" || rtype === "calendar") {
          this.activeTab = "Calendar";
        }
        else if (rtype === "payment") {
          this.activeTab = "Payment";
        }
        else if (rtype === "gmail") {
          this.activeTab = "Integrations";
        }
        else if (rtype === "billing") {
          this.activeTab = "Billing";
        }
      }
    }
    catch (e) {
      console.log("rtype error:", e);
    }


    await this.prefillUserData(); // ✅ Fetch and prefill user & business data
    await this.loadIntegrations();
    await this.loadTemplates("email", 0);
    await this.loadTemplates("notification", 0);

    var google = await checkIntegration(this.userId, "Google Calendar", "google_calendar");
    // var paypal = await checkIntegration(this.userId, "PayPal", "payment");
    // var stripe = await checkIntegration(this.userId, "Stripe", "payment");
    // var razorpay = await checkIntegration(this.userId, "Razorpay", "payment");
    // var gmail = await checkIntegration(this.userId, "Gmail", "email");
    
    this.calendars[0].connected = (google.status && google.intigration.intigration_token1.length>0);
    // this.emailIntegration.connected = gmail.status;
    // this.paymentGateways[0].connected = (paypal.status && paypal.intigration.intigration_token1.length>0);
    // this.paymentGateways[1].connected = (stripe.status && stripe.intigration.intigration_token1.length>0 &&
    //  stripe.intigration.intigration_token==="Connected");
    // this.paymentGateways[2].connected = (razorpay.status && razorpay.intigration.intigration_token1.length>0);

  },

  methods: {
    async fetchPlans() {
      try {
        this.loading = true;
        const userData = JSON.parse(sessionStorage.getItem("ldata"));
        this.user = userData || {};

        const res = await getPlanList(this.user.user_id);

        if (res.status && res.records) {
          // ✅ Normalize each plan and parse plan_features if it's a string
          this.plans = res.records.map((plan, index) => {
            let parsedFeatures = [];

            try {
              if (typeof plan.plan_features === "string") {
                parsedFeatures = JSON.parse(plan.plan_features);
              } else if (Array.isArray(plan.plan_features)) {
                parsedFeatures = plan.plan_features;
              }
            } catch (e) {
              console.warn("Invalid JSON in plan_features:", plan.plan_features);
            }

            return {
              ...plan,
              frt: parsedFeatures,
              // Keep "middle plan" highlighted (featured = true)
              featured: (index === 1 && res.records.length>2), // 0=first, 1=middle, 2=last
            };
          });
        } else {
          this.$toast?.error(res.success || "Failed to load plans");
        }
      } catch (err) {
        console.error("fetchPlans error:", err);
        this.$toast?.error("Something went wrong");
      } finally {
        this.loading = false;
      }
    },

    remainingDays() {
      if (!this.user.user_plan_expiry) return "∞";
      const today = new Date();
      const expiry = new Date(this.user.user_plan_expiry);
      const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    },

    openUpgradePopup(plan) {
      this.selectedPlan = plan;
      this.showPaymentPopup = true;
    },
    closePopup() {
      this.showPaymentPopup = false;
      this.selectedPlan = null;
    },
    async selectGateway(gateway) {
      var payUrl = "razorpay/redirectToPaymentGateway";
      if((gateway+"").localeCompare("paypal")==0){
        payUrl = "paypal/redirectToPaymentGateway";
      }
      else if((gateway+"").localeCompare("stripe")==0){
        payUrl = "stripe/redirectToPaymentGateway";
      }
      const user = JSON.parse(sessionStorage.getItem("ldata"));
      payUrl = apiBaseUrl+payUrl+"?rtype=mem&amount="+this.selectedPlan.plan_id+"&curr=USD&uid="+user.user_id;

      window.location.href = payUrl;
      
    },
    async prefillUserData() {
      try {
        this.loading = true;
        this.loaderText = "Loading profile details...";
        const res = await getUserById(this.userId);
        
        if (res!=null) {
          const u = res;
          // 🧍 Basic Profile
          this.profileForm.fullname = `${u.fname || ""} ${u.lname || ""}`.trim();
          this.profileForm.email = u.email || "";
          this.profileForm.mobile = u.mobile || "";
          if (u.avatar) this.avatarPreview = apiBaseUrl+u.avatar;

          // 🏢 Business Profile
          this.businessForm.business_name = u.business_name || "";
          this.businessForm.business_punchline = u.business_punchline || "";
          this.businessForm.business_about = u.business_about || "";
          this.businessForm.business_primary = u.business_primary || "#2563EB";
          this.businessForm.business_secondary = u.business_secondary || "#F59E0B";

          if (u.business_logo) this.businessLogoPreview = u.business_logo;
          if (u.business_fav) this.businessFavPreview = u.business_fav;

          
        }
      } catch (err) {
        console.error("Prefill Error:", err);
        this.toast.error("Failed to load profile data");
      } finally {
        this.loading = false;
      }
    },
    // =============== Profile ===============
    handleAvatarUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.avatarFile = file;
      this.avatarPreview = URL.createObjectURL(file);
    },

    async updateProfileNow() {
      if (!this.profileForm.fullname.trim()) {
        this.toast.warning("Full name is required");
        return;
      }

      if(!validateField(this.profileForm.email,"email",this.toast)){
        return;
      }

      if(!validateField(this.profileForm.mobile,"mobile",this.toast)){
        return;
      }

      this.loading = true;
      this.loaderText = "Updating profile...";
      const [fname, ...rest] = this.profileForm.fullname.split(" ");
      const lname = rest.join(" ");
      try {
        const res = await updateProfile(
          this.userId,
          fname,
          lname,
          this.profileForm.email,
          this.profileForm.mobile,
          this.avatarFile
        );
        if (res.status) this.toast.success(res.success);
        else this.toast.error(res.success);
      } catch (err) {
        console.error(err);
        this.toast.error("Profile update failed");
      } finally {
        this.loading = false;
      }
    },

    // =============== Business Profile ===============
    handleLogoUpload(e) {
      this.business_logo = e.target.files[0];
    },
    handleFavUpload(e) {
      this.business_fav = e.target.files[0];
    },

    async updateBusinessNow() {
      if (!this.businessForm.business_name.trim()) {
        this.toast.warning("Brand name required");
        return;
      }
      this.loading = true;
      this.loaderText = "Saving business settings...";
      try {
        const res = await updateBusinessProfile(
          this.userId,
          this.businessForm.business_name,
          "",
          this.businessForm.business_punchline,
          this.businessForm.business_about,
          this.businessForm.business_primary,
          this.businessForm.business_secondary,
          this.business_logo,
          this.business_fav
        );
        if (res.status) this.toast.success(res.success);
        else this.toast.error(res.success);
      } catch (err) {
        console.error(err);
        this.toast.error("Failed to update business profile");
      } finally {
        this.loading = false;
      }
    },

    // =============== Change Password ===============
    async updatePasswordNow() {
      if (!this.passwordForm.old_pass || !this.passwordForm.new_pass || !this.passwordForm.new_pass1) {
        this.toast.warning("Please enter both old and new passwords.");
        return;
      }
      
       if (this.passwordForm.old_pass.localeCompare(this.passwordForm.new_pass)==0) {
        this.toast.warning("New password should name similer to old.");
        return;
      }

       if (!this.passwordForm.new_pass.localeCompare(this.passwordForm.new_pass1)==0) {
        this.toast.warning("Please enter same new passwords.");
        return;
      }

      

      this.loading = true;
      this.loaderText = "Updating password...";
      try {
        const res = await changePassword(
          this.userId,
          this.passwordForm.old_pass,
          this.passwordForm.new_pass
        );
        if (res.status) {
          this.toast.success(res.success);
          this.passwordForm = { old_pass: "", new_pass: "" };
        } else {
          this.toast.error(res.success);
        }
      } catch (err) {
        console.error(err);
        this.toast.error("Error updating password");
      } finally {
        this.loading = false;
      }
    },

    // =============== Delete Account ===============
    async deleteAccountNow() {
      this.loading = true;
      this.loaderText = "Deactivating account...";
      try {
        const res = await deactivateAccount(this.userId);
        if (res.status) {
          this.toast.success(res.success);
          sessionStorage.clear();
          setTimeout(() => (window.location.href = "/login"), 1000);
        } else this.toast.error(res.success);
      } catch (err) {
        console.error(err);
        this.toast.error("Failed to deactivate account");
      } finally {
        this.loading = false;
        this.showDeleteDialog = false;
      }
    },

    // =============== Integrations ===============
    async loadIntegrations() {
      // Payment
      for (const pay of this.paymentGateways) {
        const check = await checkIntegration(this.userId, pay.name, "payment");
        //pay.connected = !!check.status;
        pay.connected =
        check.status &&
        check.intigration &&
        check.intigration.intigration_token1 &&
        check.intigration.intigration_token1.length > 0;
      }
      // Calendar
      for (const cal of this.calendars) {
        var gname = cal.name;
        if(gname.localeCompare("Google")==0){
          gname = "Google Calendar";
        }
        const check = await checkIntegration(this.userId, cal.name, "google_calendar");
        cal.connected = !!check.status;
      }
      // Email
      const checkEmail = await checkIntegration(this.userId, "Gmail", "email");
      this.emailIntegration.connected = !!checkEmail.status;
    },

   async toggleIntegration(type, obj) {
      if (obj.comming === true) {
        this.toast.success("This integration is coming soon!");
        return;
      }

      const name = obj.name; // PayPal, Stripe, Razorpay, Google
      const user_id = this.userId;

      this.loading = true;
      this.loaderText = (obj.connected ? "Disconnecting " : "Connecting ") + name + "...";
     
      try {
        // ================================
        // 1️⃣ IF DISCONNECT → SIMPLE API CALL
        // ================================
        if (obj.connected) {
          let url = "";
          if (name === "Google") url = apiBaseUrl + "google/calendar/disconnect";
          if (name === "Stripe") url = apiBaseUrl + "stripe1/disconnect";
          if (name === "PayPal") url = apiBaseUrl + "paypal1/disconnect";
          if (name === "Razorpay") url = apiBaseUrl + "razorpay1/disconnect";
          if (name === "Email") url = apiBaseUrl + "gmail/disconnect";


          const res = await axios.post(url, { user_id });

          if (res.data.status) {
            obj.connected = false;
            this.toast.success(name + " disconnected successfully");
          } else {
            this.toast.error(res.data.success);
          }

          return;

        } else {
          // =====
          // ===========================
          // 2️⃣ IF CONNECT → CALL THE CONNECT API
          // ================================
          let url = "";
          let body = { user_id,rtype:"settings" };

          if (name === "Google") url = apiBaseUrl + "google/calendar/connect";

          if (name === "Stripe") url = apiBaseUrl + "stripe1/connect";

          if (name === "PayPal") url = apiBaseUrl + "paypal1/connect";

         
          if (name === "Email") {
            url = apiBaseUrl + "gmail/connect";
            body = {
              user_id,
              name: this.profileForm.fullname,
              email: this.profileForm.email,
              phone: this.profileForm.mobile,
            };
          }
          
          const res = await axios.post(url, body);
          
          if (res.data.status) {
            if (name === "Google"){
              sessionStorage.removeItem("ldata");
            }
            window.location.href = res.data.url;
          } else {
            this.toast.error(res.data.success);
          }
        }
      } catch (err) {
        console.error(err);
        this.toast.error(name + " integration failed");
      } finally {
        this.loading = false;
      }
    },


    // =============== Templates ===============
   async loadTemplates(type = "email", page = 0) {
      try {
        this.loading = true;
        this.loaderText = "Loading templates...";

        const res = await getTemplates(this.userId, page, type);

        if (res && res.status) {
          if (type === "email") {
            this.emailTemplates = res.records || [];
            this.emailPage = res.page_no || 0;
            this.emailTotal = res.total || 0;
          } else if (type === "notification") {
            this.notifTemplates = res.records || [];
            this.notifPage = res.page_no || 0;
            this.notifTotal = res.total || 0;
          }
        }
      } catch (err) {
        console.error("Load Templates Error:", err);
        this.toast.error("Failed to load templates");
      } finally {
        this.loading = false;
      }
    },
async changeEmailPage(page) {
  if (page < 0) return;
  this.emailPage = page;
  await this.loadTemplates("email", page);
},

async changeNotifPage(page) {
  if (page < 0) return;
  this.notifPage = page;
  await this.loadTemplates("notification", page);
},



    editTemplate(temp, type) {
      this.templateForm = { ...temp };
      this.templateForm.temp_type = type;
      if (type === "email") this.showEmailModal = true;
      else this.showNotifModal = true;
    },

    async saveTemplate(formData) {
  if (!formData.temp_title.trim()) {
    this.toast.warning("Template title required");
    return;
  }

  this.loading = true;
  this.loaderText = "Saving template...";
  try {
    formData.temp_user = this.userId;

    const res = await createTemplate(formData);
    if (res.status) {
      this.toast.success(res.success);
      this.showEmailModal = false;
      this.showNotifModal = false;
       await this.loadTemplates("email", 0);
    await this.loadTemplates("notification", 0);
    } else this.toast.error(res.success);
  } catch (err) {
    console.error(err);
    this.toast.error("Failed to save template");
  } finally {
    this.loading = false;
  }
}
,

    async deleteTemplateNow(id) {
      if (!confirm("Are you sure you want to delete this template?")) return;
      this.loading = true;
      this.loaderText = "Deleting template...";
      try {
        const res = await deleteTemplate(id);
        if (res.status) {
          this.toast.success(res.success);
           await this.loadTemplates("email", 0);
    await this.loadTemplates("notification", 0);
        } else this.toast.error(res.success);
      } catch (err) {
        console.error(err);
        this.toast.error("Failed to delete template");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>


<style scoped>
/* === Fade Animation === */
.fade-in {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to { opacity: 1; transform: translateY(0); }
}

/* === Tabs === */
.tabs-container {
  display: flex;
  gap: 10px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.tab-item {
  padding: 10px 16px;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  font-weight: 600;
  transition: all 0.3s ease;
  color: var(--muted);
}
.tab-item.active {
  background: var(--primary);
  color: #fff;
}

/* === Card Layouts === */
.card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.card h3 { margin-bottom: 10px; color: var(--text); }

/* === Forms === */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}
.form-group label { display: block; font-weight: 600; margin-bottom: 6px; font-size: 13px; }
.form-group input, .form-group textarea {
  width: 100%; padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 6px; font-size: 13px;
}
.mt-2 { margin-top: 10px; }

/* === Buttons === */
.btn-primary, .btn-secondary, .btn-danger {
  border: none; padding: 8px 14px; border-radius: 6px;
  font-size: 13px; cursor: pointer; transition: all 0.3s ease;
}
.btn-primary { background: var(--primary); color: #fff; }
.btn-primary:hover { background: var(--primary-dark); }
.btn-secondary { background: #f3f4f6; color: var(--text); }
.btn-secondary:hover { background: #e5e7eb; }
.btn-danger { background: #dc2626; color: #fff; }
.btn-danger:hover { background: #b91c1c; }

/* === Payment Section === */
.payment-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}
.payment-card {
  background: #fff; border: 1px solid var(--border);
  border-radius: var(--radius); padding: 16px;
  display: flex; align-items: center; justify-content: space-between;
  transition: all 0.3s ease;
}
.payment-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,0.08); }
.payment-info { display: flex; align-items: center; gap: 10px; }
.payment-info i { font-size: 1.8rem; color: var(--primary); }
.connected { color: #16a34a; }
.disconnected { color: #dc2626; }

/* === Integrations === */
.integration-options {
  display: grid; gap: 12px;
}
.integration-card {
  display: flex; align-items: center; justify-content: space-between;
  background: #f9fafb; border: 1px solid var(--border);
  padding: 14px 16px; border-radius: var(--radius);
}
.integration-card i { font-size: 1.5rem; color: var(--primary); }
.switch {
  position: relative; display: inline-block; width: 38px; height: 20px;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; cursor: pointer; top: 0; left: 0;
  right: 0; bottom: 0; background-color: #ccc;
  transition: .4s; border-radius: 20px;
}
.slider:before {
  position: absolute; content: "";
  height: 14px; width: 14px; left: 3px; bottom: 3px;
  background-color: white; transition: .4s; border-radius: 50%;
}
input:checked + .slider { background-color: var(--primary); }
input:checked + .slider:before { transform: translateX(18px); }

/* === Templates Section === */
.template-list { margin-top: 12px; }
.template-item {
  display: flex; justify-content: space-between;
  align-items: center; padding: 10px 0;
  border-bottom: 1px solid var(--border);
}
.template-item h4 { margin: 0; font-size: 14px; color: var(--text); }
.template-item p { font-size: 12px; color: var(--muted); margin: 0; }

/* === Pagination === */
.pagination {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 14px; flex-wrap: wrap; gap: 10px;
}
.pagination-buttons { display: flex; align-items: center; gap: 6px; }
.pagination-buttons button {
  border: none; background: #f3f4f6; color: var(--text);
  padding: 6px 10px; border-radius: 6px; cursor: pointer; transition: all 0.3s ease;
}
.pagination-buttons button.active, .pagination-buttons button:hover {
  background: var(--primary); color: #fff;
}

/* === Account Flex Layout === */
.account-flex {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: stretch;
  margin-bottom: 24px;
}

.half-card {
  flex: 1 1 48%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* === Button Row Alignment === */
.button-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* === Button Styles === */
.btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.15);
}

.btn-primary:hover {
  background: var(--primary-dark, #1E3A8A);
  transform: translateY(-2px);
}

/* === Danger Button === */
.btn-danger {
  background: #dc2626;
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-danger:hover {
  background: #b91c1c;
  transform: translateY(-2px);
}

/* === Responsive === */
@media (max-width: 768px) {
  .half-card {
    flex: 1 1 100%;
  }
  .button-row {
    justify-content: center;
  }
}
/* === Grid Layout === */
.payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 22px;
  margin-top: 24px;
}

/* === Card Style === */
.payment-card-modern {
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  border-radius: 18px;
  box-shadow: 0 6px 20px rgba(17, 24, 39, 0.05);
  padding: 20px 22px;
  transition: all 0.35s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 180px;
}

/* Hover depth & glow */
.payment-card-modern:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.15);
  border-color: var(--primary);
}

/* === Header Section === */
.payment-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-bubble {
  background: var(--primary);
  color: #fff;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.25);
  transition: all 0.3s ease;
}

.payment-card-modern:hover .icon-bubble {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(17, 24, 39, 0.35);
}

.header-info h4 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: #111827;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
}

.status-connected {
  background: #dcfce7;
  color: #166534;
}

.status-disconnected {
  background: #fee2e2;
  color: #991b1b;
}

/* === Divider === */
.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
  opacity: 0.6;
}

/* === Footer Section === */
.payment-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

/* === Buttons Modern === */
.btn-primary-modern {
  background: var(--primary);
  color: #fff;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(17, 24, 39, 0.25);
}

.btn-primary-modern:hover {
  background: var(--primary-dark, #1e3a8a);
  transform: translateY(-2px);
}

.btn-secondary-modern {
  background: #f3f4f6;
  color: #111827;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-secondary-modern:hover {
  background: #e5e7eb;
  transform: translateY(-2px);
}

.btn-outline-modern {
  background: transparent;
  border: 1px solid #e5e7eb;
  color: var(--text);
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-outline-modern:hover {
  border-color: var(--primary);
  color: var(--primary);
}

/* === Animation === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Responsive === */
@media (max-width: 1024px) {
  .payment-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .payment-card-modern {
    min-height: 160px;
  }
}
/* === Integrations Section === */
.integrations-row {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

/* Each Integration Card 50% */
.integration-card-box {
  background: #fff;
  flex: 1 1 calc(50% - 12px);
  border-radius: 14px;
  padding: 22px 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.integration-card-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(17, 24, 39, 0.08);
  border-color: var(--primary);
}

/* === Header Section === */
.integration-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.integration-header i {
  font-size: 1.8rem;
  color: var(--primary);
  background: rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  padding: 10px;
}

.integration-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.integration-header p {
  font-size: 13px;
  color: var(--muted);
  margin-top: 4px;
}

/* === Buttons & Switch === */
.btn-primary {
  align-self: flex-start;
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: var(--primary-dark, #1e3a8a);
  transform: translateY(-2px);
}

/* Toggle Switch */
.switch {
  position: relative;
  width: 42px;
  height: 22px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  background-color: #d1d5db;
  border-radius: 22px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 0.3s;
}
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}
input:checked + .slider {
  background-color: var(--primary);
}
input:checked + .slider:before {
  transform: translateX(20px);
}

/* === Template List === */
.template-list {
  margin-top: 16px;
}
.template-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 0;
  transition: background 0.2s ease;
}
.template-item:hover {
  background: #f9fafb;
}
.template-item h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}
.template-item p {
  font-size: 12px;
  color: var(--muted);
}

/* === Pagination === */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 10px;
}
.pagination-buttons {
  display: flex;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.pagination-buttons button.active,
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* === Animation === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Responsive === */
@media (max-width: 1024px) {
  .integration-card-box {
    flex: 1 1 100%;
  }
}

/* === ✅ SETTINGS PAGE MOBILE RESPONSIVE PATCH === */
@media (max-width: 992px) {
  /* Tabs: scrollable horizontal on mobile */
  .tabs-container {
    overflow-x: auto;
    flex-wrap: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .tabs-container::-webkit-scrollbar {
    display: none;
  }
  .tab-item {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 14px;
    padding: 8px 12px;
  }

  /* Card padding & spacing */
  .card {
    padding: 16px;
    border-radius: 12px;
  }

  /* Account section layout */
  .account-flex {
    flex-direction: column;
    gap: 16px;
  }
  .half-card {
    flex: 1 1 100%;
    width: 100%;
  }
  .button-row {
    justify-content: center;
  }

  /* Payment & Calendar grids stack */
  .payment-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .payment-card-modern {
    min-height: 160px;
  }

  /* Integrations stack vertically */
  .integrations-row {
    flex-direction: column;
  }
  .integration-card-box {
    flex: 1 1 100%;
    width: 100%;
    padding: 18px;
  }

  /* Template lists spacing */
  .template-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .template-item button {
    align-self: flex-end;
  }

  /* Pagination center alignment */
  .pagination {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }

  /* Buttons adjust */
  .btn-primary,
  .btn-secondary,
  .btn-danger {
    font-size: 14px;
    padding: 10px 14px;
  }

  /* Brand inputs stack nicely */
  .form-grid {
    grid-template-columns: 1fr;
  }

  /* Tabs gap & margin adjustment */
  .tabs-container {
    margin-bottom: 16px;
    padding-bottom: 4px;
  }

  /* Full width adjustments */
  .danger-card.full-width {
    padding: 16px;
    text-align: center;
  }
  .danger-card button {
    width: 100%;
  }
}

/* === ✅ Extra Small Screens (Phones under 600px) === */
@media (max-width: 600px) {
  .settings-page {
    padding: 12px;
  }

  .tab-item {
    font-size: 13px;
    padding: 6px 10px;
  }

  .card h3 {
    font-size: 15px;
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger {
    width: 100%;
    justify-content: center;
  }

  .payment-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .integration-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .template-item h4 {
    font-size: 13px;
  }
}
.profile-pic-upload {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  position: relative;
}
.profile-pic-label {
  position: relative;
  width: 100px;
  height: 100px;
  cursor: pointer;
}
.profile-pic-frame {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid var(--primary, #111827);
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  overflow: hidden;
  position: relative;
}
.profile-pic-frame img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.default-icon {
  font-size: 2.4rem;
  color: #000;
  position: absolute;
}
.upload-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: var(--primary, #111827);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
.profile-pic-label:hover .upload-overlay {
  transform: scale(1.1);
}
#profilePicInput {
  display: none;
}
.hidden {
  display: none;
}

/* === 💬 DELETE CONFIRMATION DIALOG === */
.confirm-dialog {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.5);
  backdrop-filter: blur(3px);
  z-index: 4500;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.3s ease forwards;
}
.confirm-box {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  width: 360px;
  text-align: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.25s ease forwards;
}
.confirm-box h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.6rem;
}
.confirm-box p {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 1.4rem;
}
.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
}
.confirm-actions button {
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-cancel {
  background: #f3f4f6;
  color: #111827;
}
.btn-cancel:hover {
  background: #e5e7eb;
}
.btn-danger-confirm {
  background: #dc2626;
  color: #fff;
}
.btn-danger-confirm:hover {
  background: #b91c1c;
  transform: translateY(-2px);
}
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* === 📧 TEMPLATE MODAL === */
.template-modal {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 4600;
  animation: fadeIn 0.3s ease forwards;
}
.template-box {
  background: #fff;
  border-radius: 18px;
  padding: 1.8rem;
  width: 480px;
  max-width: 90%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.25s ease forwards;
}
.template-box h3 {
  margin-bottom: 1rem;
  color: #111827;
  font-weight: 700;
  font-size: 1.2rem;
}
.template-box .form-group {
  margin-bottom: 1rem;
}
.template-box .form-group label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.3rem;
  display: block;
}
.template-box .form-group input,
.template-box .form-group textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 0.95rem;
  transition: border 0.3s ease;
}
.template-box .form-group input:focus,
.template-box .form-group textarea:focus {
  border-color: var(--primary, #111827);
  outline: none;
}
.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1rem;
}
.template-actions .btn-primary {
  background: var(--primary, #111827);
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
}
.template-actions .btn-primary:hover {
  background: var(--primary-dark, #1E3A8A);
}
.template-actions .btn-secondary {
  background: #f3f4f6;
  color: #111827;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
}
.template-actions .btn-secondary:hover {
  background: #e5e7eb;
}

/* === ⚡SMOOTH FADE UTILITIES === */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* === RESPONSIVE PATCH === */
@media (max-width: 600px) {
  .confirm-box {
    width: 90%;
    padding: 1.5rem;
  }
  .template-box {
    width: 94%;
    padding: 1.5rem;
  }
}

/* === ⚠️ DELETE ACCOUNT CONFIRMATION MODAL === */
.confirm-overlay {
  position: fixed;
  inset: 0;
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5000;
  animation: fadeIn 0.25s ease forwards;
}

.confirm-container {
  background: #fff;
  border-radius: 18px;
  padding: 2rem 2.2rem;
  width: 420px;
  max-width: 90%;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.25s ease forwards;
}

.confirm-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #fee2e2;
  color: #fee2e2;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2.8rem;
  margin: 0 auto 1rem;
  box-shadow: 0 4px 10px rgba(220, 38, 38, 0.2);
}

.confirm-container h3 {
  margin-bottom: 0.8rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
}

.confirm-container p {
  color: #6b7280;
  font-size: 0.95rem;
  margin-bottom: 1.6rem;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-cancel {
  background: #f3f4f6;
  color: #111827;
  border: none;
  padding: 0.7rem 1.4rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-cancel:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}

.btn-danger-confirm {
  background: #dc2626;
  color: #fff;
  border: none;
  padding: 0.7rem 1.4rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-danger-confirm:hover {
  background: #b91c1c;
  transform: translateY(-1px);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* === Header Section === */
.plans-header {
  text-align: center;
  margin-bottom: 2.5rem;
  color: var(--text);
  max-width: 700px;
  margin-inline: auto;
}
.plans-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #111827;
}
.plans-header p {
  color: #6b7280;
  margin: 0.5rem 0 1.5rem;
  line-height: 1.6;
}
.cta-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* === Plans Container === */
.plans-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  justify-content: center;
  align-items: stretch;
}

/* === Plan Card === */
.plan-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
  padding: 2rem 1.5rem;
  text-align: center;
  transition: all 0.35s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.plan-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

/* === Featured Card === */
.plan-card.featured {
  border: 2px solid var(--primary);
  background: linear-gradient(135deg, #111827, #1e3a8a);
  color: #fff;
  transform: scale(1.04);
}
.plan-card.featured h3,
.plan-card.featured .duration,
.plan-card.featured .plan-features li {
  color: #fff;
}
.plan-card.featured .bx-check-circle {
  color: #dbeafe;
}
.plan-card.featured .btn-primary {
  background: #fff;
  color: #111827;
}

/* === Plan Header === */
.plan-header {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.plan-header i {
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 10px;
}
.plan-card.featured i {
  color: #fff;
}
.plan-header h3 {
  font-size: 1.3rem;
  font-weight: 700;
}
.plan-subtitle {
  font-size: 0.9rem;
  background: #facc15;
  color: #111827;
  padding: 4px 10px;
  border-radius: 999px;
  margin-top: 6px;
  font-weight: 600;
}

/* === Price Section === */
.plan-price {
  margin: 1rem 0;
}
.plan-price h2 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary);
}
.plan-card.featured .plan-price h2 {
  color: #fff;
}
.duration {
  color: #6b7280;
  font-size: 0.9rem;
}

/* === Features === */
.plan-features {
  list-style: none;
  text-align: left;
  padding: 0;
  margin: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.plan-features li {
  font-size: 0.95rem;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}
.plan-card.featured .plan-features li {
  color: #e5e7eb;
}

/* === Buttons === */
.btn-primary,
.btn-secondary {
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-primary {
  background: var(--primary, #111827);
  color: #fff;
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
}
.btn-primary:hover {
  background: #1e3a8a;
}
.btn-secondary:hover {
  background: #e5e7eb;
}
.wide-btn {
  width: 100%;
  margin-top: 1rem;
  padding: 12px;
  font-size: 15px;
}

/* === Responsive === */
@media (max-width: 768px) {
  .plans-header h2 {
    font-size: 1.5rem;
  }
  .plan-card {
    padding: 1.5rem;
  }
}

/* === Disabled Button State === */
.btn-disabled {

   border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* === Payment Popup Overlay === */
.payment-popup-overlay {
  position: fixed;
  inset: 0;
  /* background: rgba(255, 255, 255, 0.75); */
backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

/* === Payment Popup Box === */
.payment-popup {
  background: #fff;
  border-radius: 14px;
  padding: 2rem;
  width: 90%;
  max-width: 420px;
  text-align: center;
  animation: fadeInUp 0.3s ease forwards;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
}
.payment-popup h3 {
  margin-bottom: 0.5rem;
  font-weight: 700;
  color: #111827;
}
.payment-popup p {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
}

/* === Gateway Buttons === */
.gateway-options {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
}/* === Gateway Icons with Boxicons === */
.gateway {
  flex: 1;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}
.gateway:hover {
  transform: translateY(-3px);
  background: #eef2ff;
  border-color: #3b82f6;
}
.gateway-icon {
  font-size: 2.2rem;
  margin-bottom: 6px;
  display: block;
}

/* === Brand Colors === */
.gateway.razorpay .gateway-icon {
  color: #0c5394; /* Razorpay blue */
}
.gateway.paypal .gateway-icon {
  color: #00457c; /* PayPal navy */
}
.gateway.stripe .gateway-icon {
  color: #635bff; /* Stripe purple */
}
.gateway span {
  display: block;
  font-weight: 600;
  color: #111827;
}

/* === Close Button === */
.close-btn {
  width: 100%;
  margin-top: 10px;
}
</style>
